/**
 * ÖZEL YOLLAR — seviyeli özellik paketleri.
 *
 * MOTİF: 5e'de "yol" sınıfın alt sınıfıdır ve TEK seçilir. Bu ev sistemi
 * farklı çalışıyor: bir karakter istediği kadar yol alabilir. Bu yüzden yol
 * bir SEÇİM DEĞİL, bir KATMAN — her yol kendi özelliklerini ekler, hiçbiri
 * diğerinin yerine geçmez.
 *
 * SONUÇ: "Karanlık Vampir Yolu" yazan DM, bu dosyaya bir kayıt ekler. O
 * kayıt bir kez yazılır ve alan o yolu isteyen TÜM karakterlerde çalışır.
 * Karakterin kaç yol aldığı sınırsızdır.
 *
 * ÖZEL KAYNAK (KP): Yolların bir kısmı kaynak da verir (örn. Vampir KP).
 * Azami değer kaynakta tanımlı, ANLIK değer oturum durumunda tutulur —
 * kural motoru hesaplanmışı saklamaz, yalnızca girdiyi saklar.
 */

import type { PathEntry } from '@tttrpg/rules'

/**
 * VAMPIR KP — seviyeye bağlı kaynak.
 *
 * KURAL (DM'in belirlediği):
 *   • Taban: 1 KP (bir vampir yoluna girince)
 *   • Kademeli artış: seviye yükseldikçe belli eşiklerde birer artar
 *   • Üstüne GM opsiyonel olarak ekleyebilir (`CharacterInput.resourceBonuses`)
 *
 * ══ BU TABLOYU DEĞİŞTİRMEK İÇİN ══
 * Aşağıdaki `steps` tablosu KP'nin TÜM kuralıdır. Değiştirmek isterseniz
 * sadece sayıları değiştirin; kodla uğraşmanıza gerek yok.
 *
 *   { 1: 1, 3: 2, 5: 3, 8: 4, ... }
 *    ↑   ↑  ↑  ↑  ↑
 *    │   │  │  │  └── 17. seviyede 7 KP
 *    │   │  │  └───── 14. seviyede 6 KP
 *    │   │  └──────── 11. seviyede 5 KP
 *    │   └─────────── 8. seviyede 4 KP
 *    └─────────────── 5. seviyede 3 KP
 *
 * Uyarı: kural "her seviye +1" olsaydı 17. seviyede 17 KP olurdu. Ev
 * sistemlerinde bu çok hızlı büyür; bu yüzden kademeli tablo kullanıldı.
 */
const VAMPIR_KP = {
  id: 'vampir-kp',
  name: 'Vampir KP',
  scaling: {
    startsAtLevel: 1,
    baseMax: 1,
    // Kademeli: 1 → 2 → 3 → 4 → 5 → 6 → 7 (17. seviyede tavan)
    steps: { 1: 1, 3: 2, 5: 3, 8: 4, 11: 5, 14: 6, 17: 7 },
  },
  recharge: 'long' as const,
  description: 'Taban 1 KP. Seviye yükseldikçe kademeli artar; DM üstüne ekleme yapabilir.',
  showNextToHitPoints: true,
}

export const PATHS: PathEntry[] = [
  // -------------------------------------------------------------------------
  // VAMPİR YOLLARI — 1. grup. Krosmoz'daki vampir içeriğinin karşılığı.
  // -------------------------------------------------------------------------
  {
    id: 'vampir-yolu-kanli',
    name: 'Kanlı Vampir Yolu',
    description: 'Empar yeteneğiyle açılır. Yakın dövüşte ve kan yoluyla güçlenen vampirlar için.',
    raceId: 'vampir',
    features: [
      {
        id: 'kanli-empar',
        name: 'Kanlı Empar',
        description: 'Sekme yerine düşmanların canını emebilirsin. Uzun dinlenmede yenilenir.',
        grantedAtLevel: 1,
        acBonus: 1,
      },
      {
        id: 'kanli-hiz',
        name: 'Sıçrama Hızı',
        description: 'Hızına +3 ekler.',
        grantedAtLevel: 3,
      },
      {
        id: 'kanli-direnc',
        name: 'Kanlı Dayanıklılık',
        description: 'Yakın dövüş hasarına direnç.',
        grantedAtLevel: 5,
        damageResistances: ['slashing', 'piercing'],
      },
      {
        id: 'kanli-yem',
        name: 'Yaşayan Yem',
        description: 'Krit isabette hasarın yarısı kadar can geri kazanırsın.',
        grantedAtLevel: 9,
      },
      {
        id: 'kanli-yetenek',
        name: 'Yüzyılların Gücü',
        description: "Zorluk seviyesine +1. Seviye 17'de ayrıca bir ASI kazanırsın.",
        grantedAtLevel: 17,
      },
    ],
    resources: [VAMPIR_KP],
  },

  {
    id: 'vampir-yolu-karanlik',
    name: 'Karanlık Vampir Yolu',
    description: 'Gölge ve aldatma üzerine kurulu yol. Karanlık KP yarım yolda gelir.',
    raceId: 'vampir',
    features: [
      {
        id: 'karanlik-golge',
        name: 'Gölgeye Karışma',
        description: 'Karanlıkta gizlenirsin; ışıkta yakalanırsın.',
        grantedAtLevel: 1,
        skillBonuses: { stealth: 4 },
        acBonus: 1,
      },
      {
        id: 'karanlik-büyü',
        name: 'Karanlık Büyüsü',
        description: 'Gölge yaratma ve karanlık dünyasına sürünme.',
        grantedAtLevel: 3,
        innateSpells: [
          {
            grantedAtLevel: 3,
            spellIds: ['eldritch-blast'],
            uses: { count: 1, recharge: 'long' },
          },
        ],
      },
      {
        id: 'karanlik-emniyet',
        name: 'Gölge Emniyeti',
        description: 'Gölgedeyken isabet karşıtı atışlara avantaj.',
        grantedAtLevel: 5,
        acBonus: 1,
        requiresArmor: false,
      },
      {
        id: 'karanlik-yutma',
        name: 'Yaşam Yutma',
        description: 'Uzak dinlenmede KP havuzun tamamı dolar.',
        grantedAtLevel: 9,
      },
    ],
    resources: [VAMPIR_KP],
  },

  {
    id: 'vampir-yolu-kanli-kutsal',
    name: 'Kutsal Kan Vampir Yolu',
    description:
      'Kutsal kanla savaşan vampirlar için. Acı çeker ama güçlüdür. İki yolun da özelliklerini alır.',
    raceId: 'vampir',
    features: [
      {
        id: 'kutsal-kan-aydinlik',
        name: 'Kutsal Kan',
        description: 'Güneş ışığında bile hareket edebilirsin.',
        grantedAtLevel: 1,
        damageImmunities: ['radiant'],
      },
      {
        id: 'kutsal-kan-sifa',
        name: 'Kutsal Şifa',
        description:
          'Yaralarını emmek yerine kutsal kanla sarıyorsun. Uzun dinlenmede 1d10+5 can kazanırsın.',
        grantedAtLevel: 3,
      },
      {
        id: 'kutsal-kan-paradise',
        name: 'Cennet Çağrısı',
        description: 'Gölge ışığını kullanabilirsin.',
        grantedAtLevel: 5,
        innateSpells: [
          {
            grantedAtLevel: 5,
            spellIds: ['eldritch-blast'],
            uses: { count: 1, recharge: 'long' },
          },
        ],
      },
      {
        id: 'kutsal-kan-pençe',
        name: 'Pençeler',
        description: 'Doğal silah: pençe 1d6+1 kesme.',
        grantedAtLevel: 9,
        naturalWeapon: {
          name: 'Pençe',
          dice: '1d6',
          damageType: 'slashing',
          ability: 'str',
        },
      },
    ],
    resources: [VAMPIR_KP],
  },

  // -------------------------------------------------------------------------
  // ORTAK YOLLAR — her ırka açık. Bir karakter istediği kadar alabilir.
  // -------------------------------------------------------------------------
  {
    id: 'yol-eczaci',
    name: 'Eczacı Yolu',
    description: 'Sarhoşluk ve yaralara odaklanan yol. Her ırka açık.',
    features: [
      {
        id: 'eczaci-sarhosluk',
        name: 'Sarhoşluk',
        description: 'Alkolün etkisi sana dokunmaz. İçecek kullanınca kendini toparlarsın.',
        grantedAtLevel: 1,
        conditionImmunities: ['poisoned'],
      },
      {
        id: 'eczaci-ilac',
        name: 'İlaç Hazırlama',
        description: 'Dinlenme sırasında sarhoşluk etkilerini kendin temizlersin.',
        grantedAtLevel: 3,
      },
    ],
  },

  {
    id: 'yol-tezgahci',
    name: 'Tezgâhçı Yolu',
    description: 'Ölümcül yaralara ve kalıcı hasara odaklanan yol.',
    features: [
      {
        id: 'tezgahci-hayalet',
        name: 'Hayalet Gibi Acı',
        description: 'Canın 0 olduğunda bir kez ölmezsin, 1 canla kalkarsın. Seans başına bir kez.',
        grantedAtLevel: 1,
      },
      {
        id: 'tezgahci-dis',
        name: 'Diş ve Pençe',
        description: 'Yaralarını dikiyorsun. Uzun dinlenmede fazladan 1d6+3 can kazanırsın.',
        grantedAtLevel: 5,
      },
    ],
  },
]
